import {
  invalidateCache,
  readConfiguracoes,
  updateConfiguracao,
} from '@/modules/system/configuracoes/configuracoes.service';
import {
  CONFIGURACAO_BACKGROUND_JOBS_FLAGS,
  CONFIGURACAO_CANDIDATO_LGPD_RETENCAO_MESES,
  CONFIGURACAO_FOLHA_ESTAGIARIO_DOIS_EMAILS,
  CONFIGURACAO_MODULO_RELATORIOS_ATIVIDADES_HABILITADO,
  CONFIGURACAO_PROCESSO_SELETIVO_EMAIL_ADICIONAIS,
  ConfiguracoesSistema,
} from '@/modules/business/movimentacoes/enum/configuracoes-sistema.enum';
import {
  parseBackgroundJobsFlags,
  serializeBackgroundJobsFlags,
  type BackgroundJobsFlags,
} from '@/modules/system/configuracoes/background-jobs-flags.utils';
import {
  invalidateModuloRelatoriosAtividadesHabilitadoCache,
  parseValorModuloRelatoriosAtividadesHabilitado,
} from '@/modules/system/configuracoes/modulo-relatorios-atividades-habilitado';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import VisoesSistemaPanel from '@/shared/components/VisoesSistemaPanel';
import { Form } from 'react-bootstrap';
import { Fragment, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

type ConfigTab = 'sistema' | 'visoes';

function parseEmailsToText(valor: string | undefined): string {
  if (!valor?.trim()) {
    return '';
  }
  try {
    const arr = JSON.parse(valor) as unknown;
    if (!Array.isArray(arr)) {
      return '';
    }
    return arr.filter((x): x is string => typeof x === 'string').join('\n');
  } catch {
    return '';
  }
}

function textToEmailsJson(text: string): string {
  const emails = text
    .split(/[\n\r]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return JSON.stringify(emails);
}

export default function ConfiguracoesIndexPage() {
  const { setActions } = useBreadcrumbActions();
  const [activeTab, setActiveTab] = useState<ConfigTab>('sistema');
  const [loading, setLoading] = useState(false);
  const [loadingEmails, setLoadingEmails] = useState(true);
  const [savingEmails, setSavingEmails] = useState(false);
  const [emailsAdicionaisText, setEmailsAdicionaisText] = useState('');
  const [moduloRelatoriosAtividadesHabilitado, setModuloRelatoriosAtividadesHabilitado] = useState(true);
  const [loadingModuloRelatorios, setLoadingModuloRelatorios] = useState(true);
  const [savingModuloRelatorios, setSavingModuloRelatorios] = useState(false);
  const [backgroundJobsFlags, setBackgroundJobsFlags] = useState<BackgroundJobsFlags>(() =>
    parseBackgroundJobsFlags(undefined),
  );
  const [loadingBackgroundJobs, setLoadingBackgroundJobs] = useState(true);
  const [savingBackgroundJobs, setSavingBackgroundJobs] = useState(false);
  const [lgpdRetencaoMeses, setLgpdRetencaoMeses] = useState('24');
  const [loadingLgpdRetencao, setLoadingLgpdRetencao] = useState(true);
  const [savingLgpdRetencao, setSavingLgpdRetencao] = useState(false);
  const [folhaDoisEmails, setFolhaDoisEmails] = useState(true);
  const [loadingFolhaDoisEmails, setLoadingFolhaDoisEmails] = useState(true);
  const [savingFolhaDoisEmails, setSavingFolhaDoisEmails] = useState(false);

  useEffect(() => {
    readConfiguracoes()
      .then((rows) => {
        const row = rows.find((r) => r.chave === ConfiguracoesSistema.ProcessoSeletivoEmailAdicionais);
        setEmailsAdicionaisText(parseEmailsToText(row?.valor));
        const rowModulo = rows.find(
          (r) => r.chave === ConfiguracoesSistema.ModuloRelatoriosAtividadesHabilitado,
        );
        setModuloRelatoriosAtividadesHabilitado(parseValorModuloRelatoriosAtividadesHabilitado(rowModulo?.valor));
        const rowJobs = rows.find((r) => r.chave === ConfiguracoesSistema.BackgroundJobsFlags);
        setBackgroundJobsFlags(parseBackgroundJobsFlags(rowJobs?.valor));
        const rowLgpd = rows.find((r) => r.chave === ConfiguracoesSistema.CandidatoLgpdRetencaoMeses);
        const meses = rowLgpd?.valor?.trim();
        setLgpdRetencaoMeses(meses && /^\d+$/.test(meses) ? meses : '24');
        const rowFolha = rows.find((r) => r.chave === ConfiguracoesSistema.FolhaEstagiarioDoisEmails);
        setFolhaDoisEmails(rowFolha?.valor?.trim().toLowerCase() !== 'false');
      })
      .catch((err) => {
        toast.error(String(err));
      })
      .finally(() => {
        setLoadingEmails(false);
        setLoadingModuloRelatorios(false);
        setLoadingBackgroundJobs(false);
        setLoadingLgpdRetencao(false);
        setLoadingFolhaDoisEmails(false);
      });
  }, []);

  useEffect(() => {
    setActions({});
    return () => setActions({});
  }, [setActions]);

  const handleInvalidarCache = () => {
    setLoading(true);
    invalidateCache()
      .then((msg) => {
        toast.success(msg);
      })
      .catch((err) => {
        toast.error(String(err));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleToggleFolhaDoisEmails = async (habilitado: boolean) => {
    setSavingFolhaDoisEmails(true);
    try {
      await updateConfiguracao(
        ConfiguracoesSistema.FolhaEstagiarioDoisEmails,
        habilitado ? 'true' : 'false',
      );
      setFolhaDoisEmails(habilitado);
      toast.success(
        habilitado
          ? 'Notificação da folha enviada para os dois e-mails do estagiário.'
          : 'Notificação da folha enviada só para o e-mail institucional do estagiário.',
      );
    } catch (err) {
      toast.error(String(err));
    } finally {
      setSavingFolhaDoisEmails(false);
    }
  };

  const handleToggleModuloRelatoriosAtividades = async (habilitado: boolean) => {
    setSavingModuloRelatorios(true);
    try {
      await updateConfiguracao(
        ConfiguracoesSistema.ModuloRelatoriosAtividadesHabilitado,
        habilitado ? 'true' : 'false',
      );
      setModuloRelatoriosAtividadesHabilitado(habilitado);
      invalidateModuloRelatoriosAtividadesHabilitadoCache();
      toast.success(habilitado ? 'Módulo Relatórios de Atividades ativado.' : 'Módulo Relatórios de Atividades desativado.');
    } catch (err) {
      toast.error(String(err));
    } finally {
      setSavingModuloRelatorios(false);
    }
  };

  const persistBackgroundJobsFlags = async (next: BackgroundJobsFlags) => {
    setSavingBackgroundJobs(true);
    try {
      await updateConfiguracao(
        ConfiguracoesSistema.BackgroundJobsFlags,
        serializeBackgroundJobsFlags(next),
      );
      setBackgroundJobsFlags(next);
      toast.success('Configuração dos jobs em background atualizada.');
    } catch (err) {
      toast.error(String(err));
    } finally {
      setSavingBackgroundJobs(false);
    }
  };

  const handleToggleBackgroundJob = async (key: keyof BackgroundJobsFlags, enabled: boolean) => {
    const next = { ...backgroundJobsFlags, [key]: enabled };
    await persistBackgroundJobsFlags(next);
  };

  const handleSalvarLgpdRetencao = async (e: React.FormEvent) => {
    e.preventDefault();
    const valor = lgpdRetencaoMeses.trim();
    if (!/^\d+$/.test(valor) || Number(valor) < 1) {
      toast.error('Informe um número inteiro de meses (mínimo 1).');
      return;
    }
    setSavingLgpdRetencao(true);
    try {
      await updateConfiguracao(ConfiguracoesSistema.CandidatoLgpdRetencaoMeses, valor);
      toast.success('Retenção LGPD de candidatos atualizada.');
    } catch (err) {
      toast.error(String(err));
    } finally {
      setSavingLgpdRetencao(false);
    }
  };

  const handleSalvarEmailsProcessoSeletivo = (e: React.FormEvent) => {
    e.preventDefault();
    setSavingEmails(true);
    const json = textToEmailsJson(emailsAdicionaisText);
    updateConfiguracao(ConfiguracoesSistema.ProcessoSeletivoEmailAdicionais, json)
      .then(() => {
        toast.success('E-mails adicionais salvos.');
      })
      .catch((err) => {
        toast.error(String(err));
      })
      .finally(() => setSavingEmails(false));
  };

  return (
    <Fragment>
      <p className="text-muted small mb-3">
        Ações restritas a administradores (suporte).
      </p>

      <ul className="nav nav-tabs mb-3" role="tablist">
        <li className="nav-item" role="presentation">
          <button
            type="button"
            className={`nav-link${activeTab === 'sistema' ? ' active' : ''}`}
            role="tab"
            aria-selected={activeTab === 'sistema'}
            onClick={() => setActiveTab('sistema')}
          >
            Sistema
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            type="button"
            className={`nav-link${activeTab === 'visoes' ? ' active' : ''}`}
            role="tab"
            aria-selected={activeTab === 'visoes'}
            onClick={() => setActiveTab('visoes')}
          >
            Visões
          </button>
        </li>
      </ul>

      {activeTab === 'visoes' ? (
        <VisoesSistemaPanel />
      ) : (
        <>
      <div className="card mb-4">
        <div className="card-header">Jobs agendados (API)</div>
        <div className="card-body">
          <p className="card-text small text-muted mb-3">
            Chave <code>{CONFIGURACAO_BACKGROUND_JOBS_FLAGS}</code> (chave numérica{' '}
            {ConfiguracoesSistema.BackgroundJobsFlags}). Quando desativado, o cron correspondente na API não
            executa a lógica (inclui disparo manual pelo endpoint de teste, quando existir).
          </p>
          {loadingBackgroundJobs ? (
            <p className="text-muted mb-0">Carregando…</p>
          ) : (
            <div className="d-flex flex-column gap-2">
              <Form.Check
                type="switch"
                id="switch-job-folhas-mensais"
                label="Geração mensal de folhas de frequência (dia 1, 7h)"
                checked={backgroundJobsFlags.geracaoFolhasMensais}
                disabled={savingBackgroundJobs}
                onChange={(ev) => void handleToggleBackgroundJob('geracaoFolhasMensais', ev.target.checked)}
              />
              <Form.Check
                type="switch"
                id="switch-job-email-prazo-relatorio"
                label="E-mails de prazo de relatórios de atividades (diário)"
                checked={backgroundJobsFlags.emailsPrazoRelatorioAtividades}
                disabled={savingBackgroundJobs}
                onChange={(ev) =>
                  void handleToggleBackgroundJob('emailsPrazoRelatorioAtividades', ev.target.checked)
                }
              />
              <Form.Check
                type="switch"
                id="switch-job-abrir-processo-seletivo"
                label="Abrir processos seletivos agendados (a cada minuto, no horário de início)"
                checked={backgroundJobsFlags.abrirProcessosSeletivosAgendados}
                disabled={savingBackgroundJobs}
                onChange={(ev) =>
                  void handleToggleBackgroundJob('abrirProcessosSeletivosAgendados', ev.target.checked)
                }
              />
              <Form.Check
                type="switch"
                id="switch-job-fechar-processo-seletivo"
                label="Fechar processos seletivos expirados (diário, meia-noite)"
                checked={backgroundJobsFlags.fecharProcessosSeletivosExpirados}
                disabled={savingBackgroundJobs}
                onChange={(ev) =>
                  void handleToggleBackgroundJob('fecharProcessosSeletivosExpirados', ev.target.checked)
                }
              />
              <Form.Check
                type="switch"
                id="switch-job-limpeza-notificacoes"
                label="Limpar notificações com mais de 90 dias (domingo, 1h)"
                checked={backgroundJobsFlags.limpezaNotificacoesAntigas}
                disabled={savingBackgroundJobs}
                onChange={(ev) =>
                  void handleToggleBackgroundJob('limpezaNotificacoesAntigas', ev.target.checked)
                }
              />
              <Form.Check
                type="switch"
                id="switch-job-anonimizar-candidatos-lgpd"
                label="Anonimizar candidatos reprovados após prazo LGPD (dia 1 de cada mês, 3h)"
                checked={backgroundJobsFlags.anonimizarCandidatosLgpd}
                disabled={savingBackgroundJobs}
                onChange={(ev) =>
                  void handleToggleBackgroundJob('anonimizarCandidatosLgpd', ev.target.checked)
                }
              />
            </div>
          )}
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">Candidatos — retenção LGPD</div>
        <div className="card-body">
          <p className="card-text small text-muted mb-2">
            Chave <code>{CONFIGURACAO_CANDIDATO_LGPD_RETENCAO_MESES}</code> (chave numérica{' '}
            {ConfiguracoesSistema.CandidatoLgpdRetencaoMeses}). Prazo em meses para manter dados
            pessoais de candidatos reprovados ou de processos encerrados antes da anonimização
            automática (padrão 24 meses).
          </p>
          {loadingLgpdRetencao ? (
            <p className="text-muted mb-0">Carregando…</p>
          ) : (
            <form onSubmit={(e) => void handleSalvarLgpdRetencao(e)} className="row g-2 align-items-end">
              <div className="col-auto">
                <label htmlFor="lgpd-retencao-meses" className="form-label mb-0">
                  Retenção (meses)
                </label>
                <input
                  id="lgpd-retencao-meses"
                  type="number"
                  min={1}
                  className="form-control"
                  style={{ width: 120 }}
                  value={lgpdRetencaoMeses}
                  onChange={(ev) => setLgpdRetencaoMeses(ev.target.value)}
                  disabled={savingLgpdRetencao}
                />
              </div>
              <div className="col-auto">
                <button type="submit" className="btn btn-primary" disabled={savingLgpdRetencao}>
                  {savingLgpdRetencao ? 'Salvando…' : 'Salvar'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">Folha de frequência — e-mails do estagiário</div>
        <div className="card-body">
          <p className="card-text small text-muted mb-2">
            Chave <code>{CONFIGURACAO_FOLHA_ESTAGIARIO_DOIS_EMAILS}</code> (chave numérica{' '}
            {ConfiguracoesSistema.FolhaEstagiarioDoisEmails}). Quando ligado, a notificação da folha
            vai para o e-mail institucional e o pessoal do estagiário. Se os dois forem iguais, só o
            institucional recebe. Desligado, permanece só o institucional (ou o pessoal, se o
            institucional estiver vazio).
          </p>
          {loadingFolhaDoisEmails ? (
            <p className="text-muted mb-0">Carregando…</p>
          ) : (
            <Form.Check
              type="switch"
              id="switch-folha-estagiario-dois-emails"
              label="Enviar a notificação da folha para o e-mail institucional e o pessoal"
              checked={folhaDoisEmails}
              disabled={savingFolhaDoisEmails}
              onChange={(ev) => void handleToggleFolhaDoisEmails(ev.target.checked)}
            />
          )}
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">Módulos — Relatórios de Atividades</div>
        <div className="card-body">
          <p className="card-text small text-muted mb-2">
            Chave <code>{CONFIGURACAO_MODULO_RELATORIOS_ATIVIDADES_HABILITADO}</code> (chave numérica{' '}
            {ConfiguracoesSistema.ModuloRelatoriosAtividadesHabilitado}). Quando desativado, o módulo
            some do menu lateral e as rotas do relatório redirecionam para o início.
          </p>
          {loadingModuloRelatorios ? (
            <p className="text-muted mb-0">Carregando…</p>
          ) : (
            <Form.Check
              type="switch"
              id="switch-modulo-relatorios-atividades"
              label="Exibir e permitir acesso ao módulo Relatórios de Atividades"
              checked={moduloRelatoriosAtividadesHabilitado}
              disabled={savingModuloRelatorios}
              onChange={(ev) => void handleToggleModuloRelatoriosAtividades(ev.target.checked)}
            />
          )}
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">Processo seletivo — e-mails em cópia</div>
        <div className="card-body">
          <p className="card-text small text-muted mb-2">
            Chave <code>{CONFIGURACAO_PROCESSO_SELETIVO_EMAIL_ADICIONAIS}</code> (chave numérica{' '}
            {ConfiguracoesSistema.ProcessoSeletivoEmailAdicionais}). Endereços notificados ao criar um
            processo seletivo, além do e-mail do usuário criador.
          </p>
          {loadingEmails ? (
            <p className="text-muted mb-0">Carregando…</p>
          ) : (
            <form onSubmit={handleSalvarEmailsProcessoSeletivo}>
              <label htmlFor="emails-processo-seletivo" className="form-label">
                Um e-mail por linha
              </label>
              <textarea
                id="emails-processo-seletivo"
                className="form-control font-monospace"
                rows={6}
                value={emailsAdicionaisText}
                onChange={(ev) => setEmailsAdicionaisText(ev.target.value)}
                spellCheck={false}
              />
              <button type="submit" className="btn btn-primary mt-3" disabled={savingEmails}>
                {savingEmails ? 'Salvando…' : 'Salvar e-mails'}
              </button>
            </form>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">Cache</div>
        <div className="card-body">
          <p className="card-text">
            Invalidar o cache faz com que as próximas consultas (ex.: listagem de comarcas) busquem
            os dados atualizados no banco. Use quando houver alteração direta no banco ou quando o
            suporte orientar.
          </p>
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={handleInvalidarCache}
            disabled={loading}
          >
            {loading ? 'Invalidando...' : 'Invalidar cache'}
          </button>
        </div>
      </div>
        </>
      )}
    </Fragment>
  );
}
